import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, KeyRound, Pencil, Trash2, Webhook } from '@/components/icons';
import { CopyField } from '@/components/CopyField';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { formatInZone, triggerWhen, webhookUrl } from '@/lib/triggers';
import type { Project, Trigger } from '@/lib/types';
import { Badge, Button, Card, Dialog, EmptyState, ErrorNotice, PageLoader, StatusPill, Toggle, useConfirm, useToast } from '@/components/ui';
import { TriggerDialog } from './TriggerDialog';

/** How to call a webhook. The token is only known right after creating it or replacing it. */
function WebhookSetup({ trigger, token, onClose, onRotate, canRotate, rotating }: { trigger: Trigger; token?: string; onClose: () => void; onRotate: () => void; canRotate: boolean; rotating: boolean }) {
  const url = webhookUrl(trigger.id);
  const shownToken = token ?? `${trigger.tokenPrefix ?? '1qa_wh_'}… (hidden)`;
  return (
    <Dialog open onClose={onClose} size="lg" title={`Webhook: ${trigger.name}`} description="Call this URL with the token to run it. Any JSON body is accepted (GitLab’s payload works as is).">
      <div className="space-y-4 text-sm">
        <CopyField label="URL (POST)" value={url} />
        {token ? (
          <>
            <CopyField label="Secret token" value={token} secret />
            <p className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-900">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" /> Copy the token now - it is shown only once. Keep it secret: anyone with it can start this run.
            </p>
          </>
        ) : (
          <p className="text-slate-500">
            The token starts with <code>{trigger.tokenPrefix}</code> and is not shown again. Lost it? Replace it below - the old one stops working at once.
          </p>
        )}
        <div className="space-y-1">
          <div className="text-xs font-medium text-slate-500">From a script or CI job</div>
          <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{`curl --fail -X POST -H "Authorization: Bearer ${shownToken}" \\\n  ${url}`}</pre>
          <p className="text-xs text-slate-500">
            The answer contains <code>statusUrl</code>: GET it with the same token to wait for the result (QUEUED, RUNNING, then PASSED / FAILED / ERROR).
          </p>
          <p className="text-xs text-slate-500">
            Run against another environment by adding its test data collection: <code>{`${url}?collection=Staging`}</code>
            {trigger.collectionName ? ` (default: ${trigger.collectionName}).` : ' (default: no collection, shared values only).'}
          </p>
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium text-slate-500">From GitLab (project Settings → Webhooks)</div>
          <ol className="list-decimal space-y-0.5 pl-5 text-slate-600 dark:text-slate-300">
            <li>URL: the URL above. Secret token: the token.</li>
            <li>Choose the events, for example “Deployment events” or “Pipeline events”, and save.</li>
          </ol>
        </div>
        {canRotate && (
          <Button variant="outline" size="sm" onClick={onRotate} loading={rotating} icon={<KeyRound className="size-4" />}>
            Replace token
          </Button>
        )}
      </div>
    </Dialog>
  );
}

/** Schedules and webhooks that run the project's flows and groups automatically. */
export function AutomationTab({ project, canWrite }: { project: Project; canWrite: boolean }) {
  const { canModify } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const triggers = useQuery({ queryKey: ['triggers', project.id], queryFn: () => api.triggers(project.id), refetchInterval: 30_000 });
  const flows = useQuery({ queryKey: ['flows', project.id], queryFn: () => api.flows(project.id) });
  const groups = useQuery({ queryKey: ['groups', project.id], queryFn: () => api.groups(project.id) });
  const [editing, setEditing] = useState<{ kind: 'SCHEDULE' | 'WEBHOOK'; trigger?: Trigger } | null>(null);
  const [setup, setSetup] = useState<{ trigger: Trigger; token?: string } | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['triggers', project.id] });
  const toggle = useMutation({
    mutationFn: (t: Trigger) => api.updateTrigger(t.id, { enabled: !t.enabled }),
    onSuccess: (t) => {
      toast.success(t.enabled ? `“${t.name}” turned on` : `“${t.name}” turned off`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (t: Trigger) => api.deleteTrigger(t.id),
    onSuccess: () => {
      toast.success('Deleted');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const rotate = useMutation({
    mutationFn: (t: Trigger) => api.rotateTriggerToken(t.id),
    onSuccess: (t) => {
      setSetup({ trigger: t, token: t.token });
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (triggers.isPending) return <PageLoader />;
  if (triggers.isError) return <ErrorNotice error={triggers.error} onRetry={() => triggers.refetch()} />;

  const nothingToRun = (flows.data?.length ?? 0) === 0 && (groups.data?.length ?? 0) === 0;
  const buttons = canWrite && (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" onClick={() => setEditing({ kind: 'SCHEDULE' })} disabled={nothingToRun} icon={<CalendarClock className="size-4" />}>
        New schedule
      </Button>
      <Button variant="outline" onClick={() => setEditing({ kind: 'WEBHOOK' })} disabled={nothingToRun} icon={<Webhook className="size-4" />}>
        New webhook
      </Button>
    </div>
  );

  const dialogs = (
    <>
      {editing && (
        <TriggerDialog
          key={editing.trigger?.id ?? editing.kind}
          projectId={project.id}
          kind={editing.kind}
          trigger={editing.trigger}
          flows={flows.data ?? []}
          groups={groups.data ?? []}
          onClose={() => setEditing(null)}
          onCreated={(t) => t.kind === 'WEBHOOK' && setSetup({ trigger: t, token: t.token })}
        />
      )}
      {setup && (
        <WebhookSetup
          trigger={setup.trigger}
          token={setup.token}
          onClose={() => setSetup(null)}
          canRotate={canModify('test.write', setup.trigger.createdById, project.orgId)}
          rotating={rotate.isPending}
          onRotate={async () => {
            const ok = await confirm({ title: 'Replace the token?', message: 'The current token stops working immediately. Update GitLab or your scripts with the new one.', confirmLabel: 'Replace token' });
            if (ok) rotate.mutate(setup.trigger);
          }}
        />
      )}
    </>
  );

  if (triggers.data.length === 0) {
    return (
      <>
        {dialogs}
        <EmptyState
          icon={<CalendarClock className="size-6" />}
          title="Nothing runs automatically yet"
          description={
            nothingToRun
              ? 'Create a test group or a flow first, then run it on a schedule or from a webhook.'
              : 'Run a flow or a group on a schedule - for example every night at 02:00 - or from a webhook after each deploy.'
          }
          action={buttons}
        />
      </>
    );
  }

  return (
    <Card>
      {dialogs}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-slate-800">
        <p className="text-sm text-slate-500">Runs start as the person who created the schedule or webhook.</p>
        <div className="ml-auto">{buttons}</div>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {triggers.data.map((t) => {
          const mine = canModify('test.write', t.createdById, project.orgId);
          const Icon = t.kind === 'SCHEDULE' ? CalendarClock : Webhook;
          return (
            <li key={t.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
              <span className="grid size-9 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300">
                <Icon className="size-[18px]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900 dark:text-white">
                  {t.name}
                  {!t.enabled && <Badge>Off</Badge>}
                </div>
                <div className="truncate text-xs text-slate-500">
                  Runs {t.targetType === 'FLOW' ? 'flow' : 'group'} “{t.targetName ?? 'deleted'}”{t.collectionName && ` with ${t.collectionName} test data`} · {triggerWhen(t)}
                  {t.createdBy && ` · by ${t.createdBy.name}`}
                  {t.notify === 'FAILURES' ? ' · notifies on failure' : t.notify === 'ALWAYS' ? ' · notifies after every run' : ''}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {t.kind === 'SCHEDULE' && t.enabled && t.nextRunAt && `Next: ${formatInZone(t.nextRunAt, t.timezone ?? 'UTC')}`}
                  {t.lastFiredAt && `${t.kind === 'SCHEDULE' && t.enabled && t.nextRunAt ? ' · ' : ''}Last started ${timeAgo(t.lastFiredAt)}`}
                </div>
                {t.lastNotifyError && (
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="size-3.5" /> Notification not delivered - {t.lastNotifyError}
                  </div>
                )}
                {t.lastError && (
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="size-3.5" /> {t.lastError}
                  </div>
                )}
              </div>
              {t.lastRun && (
                <Link to={t.lastRun.kind === 'flow' ? `/flow-runs/${t.lastRun.id}` : `/runs/${t.lastRun.id}`} className="text-xs" title="Open the last run">
                  {t.lastRun.status ? <StatusPill status={t.lastRun.status} /> : 'Last run'}
                </Link>
              )}
              <div className="flex items-center gap-1">
                {mine && <Toggle checked={t.enabled} onChange={() => toggle.mutate(t)} label={`${t.enabled ? 'Turn off' : 'Turn on'} ${t.name}`} />}
                {t.kind === 'WEBHOOK' && (
                  <Button size="sm" variant="ghost" onClick={() => setSetup({ trigger: t })}>
                    Setup
                  </Button>
                )}
                {mine && (
                  <>
                    <Button size="sm" variant="ghost" aria-label={`Edit ${t.name}`} onClick={() => setEditing({ kind: t.kind, trigger: t })} icon={<Pencil className="size-4" />} />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Delete ${t.name}`}
                      onClick={async () => {
                        const ok = await confirm({ title: `Delete “${t.name}”?`, message: t.kind === 'WEBHOOK' ? 'Calls to its URL will stop working.' : 'It will not run any more. Past runs stay.', confirmLabel: 'Delete' });
                        if (ok) remove.mutate(t);
                      }}
                      icon={<Trash2 className="size-4 text-rose-500" />}
                    />
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

